// События namespace `profile`: SET наружу, Socket.IO — в открытую вкладку и
// одно durable-событие — втягивание чужой картинки в наше хранилище.
import { z } from "zod";
import {
	IsoDateTimeSchema,
	LocaleSchema,
	SubjectSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";
import { AgeGroupSchema } from "./schemas.js";

/**
 * SET `profile-updated` — сервисы держат кэш профиля с TTL и обновляют его
 * по этому событию. Кэш без инвалидации расходится ровно тогда, когда это
 * заметит пользователь (rules/lessons-learned.md §8).
 *
 * Поля не дублируются целиком: рассылается список изменившихся полей, сервис
 * перечитывает userinfo. Так профиль не размазывается по журналам доставки.
 */
export const ProfileUpdatedSetPayloadSchema = z.object({
	subject: z.object({ format: z.literal("opaque"), id: SubjectSchema }),
	/** Какие поля профиля изменились (`firstName`, `avatarUrl`, `locale`, …). */
	fields: z.array(z.string()).min(1),
	/** Дублируется явно: сервисы обязаны читать возраст, а не спрашивать заново. */
	ageGroup: AgeGroupSchema.optional(),
	locale: LocaleSchema.optional(),
	updatedAt: IsoDateTimeSchema,
});

/** URI типа события в реестре SET экосистемы. */
export const PROFILE_UPDATED_SET_URI = "https://id.iec.exchange/secevent/profile-updated";

// ─── Socket.IO ──────────────────────────────────────────────────────────────

/** Профиль изменён (в т.ч. из другой вкладки) — витрина перечитывает данные. */
export const ProfileUpdatedSocketEventSchema = z.object({
	channel: z.literal("user"),
	event: z.literal("profile:updated"),
	payload: z.object({
		fields: z.array(z.string()).min(1),
		version: z.number().int().positive(),
		updatedAt: IsoDateTimeSchema,
	}),
});

/** Аватар обработан (ресайз + варианты) — карточка меняет картинку без релоада. */
export const AvatarReadySocketEventSchema = z.object({
	channel: z.literal("user"),
	event: z.literal("profile:avatar-ready"),
	payload: z.object({
		avatarUrl: z.url(),
		updatedAt: IsoDateTimeSchema,
	}),
});

// ─── Inngest ────────────────────────────────────────────────────────────────
//
// Единственное durable-событие модуля. Остальное профилю не нужно:
//   • изменение профиля → SET `profile-updated` подключённым сервисам
//     (`emitSetEvent`, модуль `events`);
//   • финал онбординга → `points/earn.requested` (единственный эмитент Баллов
//     — модуль `points`, инвариант №9);
//   • свой аватар человек грузит presigned PUT и фиксирует ключ синхронно.
//
// Раньше здесь были объявлены `profile/avatar.uploaded`,
// `profile/onboarding.completed` и `profile/updated` — ни одного из них не
// существовало в `inngest/client.ts`, ни одно не эмитилось и ни одно не имело
// подписчика (В7). Контракт, описывающий несуществующее, хуже отсутствующего.

/**
 * Втянуть картинку провайдера входа (Google `picture`) в наше хранилище как
 * аватар. Имя и форма ОБЯЗАНЫ совпадать с каталогом `inngest/client.ts` —
 * сверяется гейтом `events-contract.test.ts`.
 *
 * Зачем событие, а не синхронная загрузка при входе: чужой хост может
 * ответить медленно или не ответить вовсе, а вход из-за картинки ждать не
 * должен. Durable-функция тянет с ретраями; до её завершения публичный
 * аватар — наша заглушка, а не редирект на чужой хост (`resolvePublicAvatar`).
 */
export const AvatarImportRequestedEventSchema = z.object({
	name: z.literal("profile/avatar.import.requested"),
	data: z.object({
		userId: UuidV7Schema,
		/** Откуда тянуть. Только `https` и только хосты провайдеров входа. */
		sourceUrl: z.url(),
		requestedAt: IsoDateTimeSchema,
	}),
});
